import { Outcome } from "@ethossoftworks/outcome"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimeSeriesPoint {
    timestamp: string
    avgMs: number
}

export interface PerformanceOverview {
    avgPageLoadMs: number
    p95PageLoadMs: number
    totalPageViews: number
}

export interface PageLoadTrend {
    points: TimeSeriesPoint[]
}

export interface PagePerformanceRow {
    pageName: string
    avgLoadMs: number
    views: number
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const APP_INSIGHTS_API = "https://api.applicationinsights.io/v1/apps"

export class PerformanceService {
    private appId: string
    private apiKey: string

    constructor() {
        this.appId = process.env.APPINSIGHTS_APP_ID as string
        this.apiKey = process.env.APPINSIGHTS_API_KEY as string
    }

    isConfigured(): boolean {
        return !!this.appId && !!this.apiKey
    }

    /**
     * Query App Insights REST API.
     * @param kustoQuery  KQL query string
     * @param timespan    ISO 8601 duration, e.g. "PT24H" or "P30D"
     */
    private async query(kustoQuery: string, timespan: string): Promise<Outcome<AppInsightsRow[], string>> {
        if (!this.isConfigured()) {
            return Outcome.error("App Insights not configured")
        }

        try {
            const url =
                `${APP_INSIGHTS_API}/${this.appId}/query` +
                `?timespan=${encodeURIComponent(timespan)}` +
                `&query=${encodeURIComponent(kustoQuery)}`

            const response = await fetch(url, {
                headers: { "x-api-key": this.apiKey },
            })

            if (!response.ok) {
                const body = await response.text().catch(() => "")
                console.warn("App Insights query failed", response.status, body)
                return Outcome.error(`Query failed (${response.status})`)
            }

            const data: AppInsightsResponse = await response.json()
            return Outcome.ok(data.tables[0]?.rows ?? [])
        } catch (e) {
            return Outcome.error(`Network error: ${String(e)}`)
        }
    }

    // -----------------------------------------------------------------------
    // Overview (24 h)
    // -----------------------------------------------------------------------

    async fetchOverview(): Promise<Outcome<PerformanceOverview, string>> {
        // Simple query against nav timing — always has data
        const kql = `
            customMetrics
            | where name == "nav_loadEventEnd_ms"
            | summarize avgLoad=avg(value), p95Load=percentile(value, 95), cnt=count()
        `
        const outcome = await this.query(kql, "PT24H")
        if (outcome.isError()) return Outcome.error(outcome.error)

        const rows = outcome.value
        if (rows.length === 0 || Number(rows[0][2]) === 0) {
            return Outcome.ok({ avgPageLoadMs: 0, p95PageLoadMs: 0, totalPageViews: 0 })
        }

        return Outcome.ok({
            avgPageLoadMs: Number(rows[0][0]) || 0,
            p95PageLoadMs: Number(rows[0][1]) || 0,
            totalPageViews: Number(rows[0][2]) || 0,
        })
    }

    // -----------------------------------------------------------------------
    // 24-hour trend (hourly buckets)
    // -----------------------------------------------------------------------

    async fetchLast24HoursTrend(): Promise<Outcome<PageLoadTrend, string>> {
        const kql = `
            customMetrics
            | where name == "nav_loadEventEnd_ms"
            | summarize avgLoad=avg(value) by bin(timestamp, 1h)
            | order by timestamp asc
        `
        const outcome = await this.query(kql, "PT24H")
        if (outcome.isError()) return Outcome.error(outcome.error)

        return Outcome.ok({
            points: outcome.value.map((row) => ({
                timestamp: String(row[0]),
                avgMs: Number(row[1]) || 0,
            })),
        })
    }

    // -----------------------------------------------------------------------
    // 30-day trend (daily buckets)
    // -----------------------------------------------------------------------

    async fetchLast30DaysTrend(): Promise<Outcome<PageLoadTrend, string>> {
        const kql = `
            customMetrics
            | where name == "nav_loadEventEnd_ms"
            | summarize avgLoad=avg(value) by bin(timestamp, 1d)
            | order by timestamp asc
        `
        const outcome = await this.query(kql, "P30D")
        if (outcome.isError()) return Outcome.error(outcome.error)

        return Outcome.ok({
            points: outcome.value.map((row) => ({
                timestamp: String(row[0]),
                avgMs: Number(row[1]) || 0,
            })),
        })
    }

    // -----------------------------------------------------------------------
    // Per-page breakdown
    // -----------------------------------------------------------------------

    async fetchPerPageMetrics(): Promise<Outcome<PagePerformanceRow[], string>> {
        const kql = `
            pageViews
            | summarize avgDuration=avg(duration), viewCount=count() by name
        `
        const outcome = await this.query(kql, "P30D")
        if (outcome.isError()) return Outcome.error(outcome.error)

        const rows: PagePerformanceRow[] = outcome.value
            .map((row) => ({
                pageName: cleanPageName(String(row[0])),
                avgLoadMs: Number(row[1]) || 0,
                views: Number(row[2]) || 0,
            }))
            .filter((r) => r.views > 0)
            .sort((a, b) => b.views - a.views)

        return Outcome.ok(rows)
    }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip the common "Gauge Telematics - " prefix for cleaner display */
function cleanPageName(raw: string): string {
    return raw.replace(/^Gauge Telematics\s*-\s*/, "")
}

// ---------------------------------------------------------------------------
// App Insights REST API response types
// ---------------------------------------------------------------------------

type AppInsightsRow = (string | number | null)[]

interface AppInsightsResponse {
    tables: Array<{
        name: string
        columns: Array<{ name: string; type: string }>
        rows: AppInsightsRow[]
    }>
}
